import type { StatIconName } from "../components/StatIcon.js";

type PatchValue =
  | { kind: "pool"; label: string }
  | { kind: "stat"; stat: Extract<StatIconName, "action" | "fatigue">; amount: number };

type CardPatchChange = {
  kind: "card";
  cardId: string;
  cardName: string;
  title: string;
  before: readonly PatchValue[];
  after: readonly PatchValue[];
};

type SummaryPatchChange = {
  kind: "summary";
  topic?: "event" | "stage";
  name: string;
  title: string;
  description?: string;
};

type PatchChange = CardPatchChange | SummaryPatchChange;

export function patchSummaryIcon(topic?: SummaryPatchChange["topic"]) {
  return topic === "stage" ? "/icon-stage.png" : "/icon-event.png";
}

export type PatchNote = {
  date: string;
  title: string;
  changes: readonly PatchChange[];
};

export const PATCH_NOTES: readonly PatchNote[] = [
  {
    date: "2026.09.04", title: "第20期の報酬プール",
    changes: [
      { kind: "summary", topic: "stage", name: "中規模報酬", title: "現場裁量を方針整理・業務復旧へ差し替え", description: "行動+2・進捗+1・防御+1の現場裁量に代わり、2枚引いて行動+2・手札を2枚捨てる方針整理と、手札が5枚になるまで引き行動+1の業務復旧が出現します。" },
      { kind: "summary", topic: "stage", name: "小規模報酬", title: "産業医をプールから削除", description: "小規模報酬の労働者は中堅のみになります。産業医は中規模報酬で引き続き出現します。" },
    ],
  },
  {
    date: "2026.09.04", title: "第20期の組織基盤",
    changes: [
      { kind: "summary", topic: "stage", name: "投資推進室", title: "拡大戦略室と差し替えて追加", description: "このPJで投資施策を累計3回使っていれば、毎フェーズの行動+1。投資に使った手数を回収する、第20期向けの新しい組織基盤です。第20期では新卒を増やす報酬がなく、拡大戦略室が発動しないため差し替えました。" },
    ],
  },
  {
    date: "2026.09.03", title: "第20期の報酬",
    changes: [
      { kind: "summary", topic: "stage", name: "中規模報酬", title: "朝会を現場裁量へ差し替え", description: "朝会の代わりに、行動+2・進捗+1・防御+1を得る現場裁量が出現します。" },
      { kind: "summary", topic: "stage", name: "中規模報酬", title: "中堅を産業医へ差し替え", description: "中規模報酬の労働者は産業医のみになり、疲労回復を取りにいく選択を強めています。" },
    ],
  },
  {
    date: "2026.09.03", title: "第20期の難度",
    changes: [
      { kind: "summary", topic: "stage", name: "第20期「投資と回収」", title: "中盤の要求進捗と後半工期を調整", description: "中盤の必要進捗の伸びを緩やかにし、後半PJの工期を7〜8へ延長しました。投資を仕込んだ後に回収するための余裕を増やしています。" },
    ],
  },
  {
    date: "2026.09.03", title: "投資施策",
    changes: [
      { kind: "summary", name: "AI導入・顧問契約・危機管理室の設置", title: "使用したラウンドから発動", description: "投資施策の進捗・ドロー・防御は、次ラウンドを待たず使用したラウンドから得られます。以後もPJ終了まで毎ラウンド発動します。" },
    ],
  },
  {
    date: "2026.09.02", title: "投資施策と第20期",
    changes: [
      { kind: "summary", topic: "stage", name: "第20期「投資と回収」", title: "追加", description: "案件負荷を疲労・手札妨害・進捗戻し・納期圧力の系統に分け、デッキに合わせて行き先を選べる新ステージです。危機管理室は小規模、納期交渉は中規模、顧問契約は大規模報酬へ配置。小規模には業務フロー整備（1枚引く・行動+2）も入ります。" },
    ],
  },
  {
    date: "2026.09.01", title: "カード",
    changes: [
      {
        kind: "summary",
        name: "心理カウンセリング・引き抜き採用",
        title: "ベテランを捨て札へ",
        description: "ベテランは手札ではなく捨て札に加わります。今すぐ使う強さを下げました。",
      },
    ],
  },
  {
    date: "2026.08.31", title: "窓際の活用カード",
    changes: [
      { kind: "summary", topic: "stage", name: "第19期「再配置と活用」", title: "追加", description: "窓際を変換する・残して活かす・負債として引き受ける、三つの人材活用ルートを持つ新ステージを追加しました。ハプニングマスを多めに配置しています。" },
      { kind: "summary", name: "スポット起用", title: "小規模報酬に追加", description: "手札に窓際があると進捗を得る、窓際を活かす施策を追加しました。" },
      { kind: "summary", name: "心理カウンセリング・特命チーム編成・引き抜き採用", title: "中規模報酬に追加", description: "窓際をベテランへ変える、PJ限定窓際を含めて立て直す、窓際を抱える代わりに即戦力を得る施策を追加しました。" },
    ],
  },
  {
    date: "2026.08.30", title: "第18期「採用と標準化」",
    changes: [
      { kind: "summary", topic: "stage", name: "第18期「採用と標準化」", title: "追加", description: "新卒を増やし、偏った手札を運用・標準化で出力へ変える新ステージを追加しました。" },
      { kind: "summary", name: "配属計画", title: "効果を調整", description: "1枚引く効果を追加しました。新卒・窓際だけの手札を整えながら、条件達成を狙えます。" },
      { kind: "summary", topic: "stage", name: "採用ルート", title: "組織基盤の取得機会を追加", description: "採用拡大・受け入れ支援の寄り道に基盤マスを置き、後半の大型PJでも組織基盤を得られます。" },
      { kind: "summary", topic: "stage", name: "ハプニング", title: "採用過多向けの負荷を追加", description: "一時的な窓際追加、施策ドロー制限、対応事項の滞留を組み合わせた専用の負荷構成です。" },
    ],
  },
  {
    date: "2026.08.29", title: "呼称の整理",
    changes: [
      {
        kind: "summary",
        name: "用語",
        title: "案件をプロジェクト（PJ）に変更",
        description: "マス種別はPJ、本文ではプロジェクトと書きます。カードのPJ限定は従来どおりです。",
      },
    ],
  },
  {
    date: "2026.08.29", title: "プロジェクト開始の山札",
    changes: [
      {
        kind: "summary",
        name: "デッキ循環",
        title: "プロジェクト開始時に所持カードを山札へまとめる",
        description: "前のプロジェクトの捨て札や山札の残りは引き継ぎません。プロジェクトの途中では、従来どおり山札が尽きたときだけ捨て札をシャッフルします。",
      },
    ],
  },
  {
    date: "2026.08.28", title: "第17期「継承と統合」",
    changes: [
      { kind: "summary", topic: "stage", name: "第17期「継承と統合」", title: "追加", description: "継承・標準化・協業の3ルートから、次期体制への完全移行を目指します。" },
      { kind: "summary", topic: "stage", name: "第17期「継承と統合」", title: "大型プロジェクトを後半へ配置", description: "大型報酬を得るプロジェクトは、デッキを育てた7つクリア後から登場します。序盤は中小規模PJで進めます。" },
      { kind: "summary", topic: "stage", name: "第17期「継承と統合」", title: "序盤の負荷を調整", description: "序盤の手札妨害・滞留・不要人員の負荷と必要進捗を見直し、対策札を選んで進める余地を広げました。" },
      { kind: "summary", topic: "stage", name: "第17期「継承と統合」", title: "中盤に基盤マスを追加", description: "高負荷の記録統合・連携拡張ルートに、組織基盤を得られる寄り道を追加しました。" },
    ],
  },
  {
    date: "2026.08.28", title: "カードと手札妨害",
    changes: [
      { kind: "summary", name: "業務復旧", title: "中規模報酬に追加", description: "手札が5枚になるまで引き、行動+1。第15期・第16期の中規模報酬にも追加しました。" },
      { kind: "summary", name: "緊急配置転換", title: "対象を拡張", description: "労働者だけでなく、手札の任意のカード1枚を捨てて2枚引けるようになりました。" },
      { kind: "summary", name: "手札妨害", title: "仕様変更", description: "手札枚数を減らす代わりに、通常補充後の手札から2～3枚を選んで捨てる方式へ変更しました。" },
      { kind: "summary", name: "手札選択", title: "UI改善", description: "手札妨害時に捨てるカードの枚数と選択状況を表示します。" },
    ],
  },
  {
    date: "2026.08.27", title: "第16期「障害と復旧」",
    changes: [
      { kind: "summary", topic: "stage", name: "第16期「障害と復旧」", title: "追加" },
    ],
  },
  {
    date: "2026.08.27", title: "持ち込み報酬",
    changes: [
      { kind: "summary", topic: "stage", name: "有給休暇", title: "16ステージクリア報酬に追加", description: "異なる16ステージをクリアすると、作戦会議で「有給休暇」を持ち込めるようになります。ステージ選択には次の報酬と解放までの進捗を表示します。" },
    ],
  },
  {
    date: "2026.08.26", title: "第15期・ハプニングの事前情報",
    changes: [
      { kind: "summary", topic: "stage", name: "第15期「予測と対策」", title: "追加" },
      { kind: "summary", topic: "stage", name: "作戦会議・マップ", title: "ハプニングの特徴を事前公開", description: "作戦会議とマップのプロジェクト詳細に、起こり得るハプニングの性質と数値を表示します。内訳や対策の解説は表示しません。次のハプニングを確定で予告するものではありません。" },
    ],
  },
  {
    date: "2026.08.26",
    title: "カード・ハプニングの試作",
    changes: [
      { kind: "summary", name: "業務切り分け", title: "共通の中規模報酬に追加", description: "1ドロー・行動+1。手札を最大1枚、プロジェクト終了まで一時除外できます。防御は増えません。第11〜14期の専用報酬プールは変更しません。" },
      { kind: "summary", topic: "event", name: "対応事項", title: "新しい妨害を試作", description: "行動を使って処理する「差し戻し対応」、PJ限定の窓際追加、次ラウンドの施策ドロー制限を用意しました。PJ限定カードは終了時にすべて消えます。第1〜14期のプロジェクトには配置しません。" },
    ],
  },
  {
    date: "2026.08.26",
    title: "ステージ",
    changes: [
      { kind: "summary", topic: "stage", name: "第14期「長期持久戦」", title: "プロジェクト難度を再調整", description: "序盤から中盤にかけての必要進捗を、到達時のクリア数と負荷に合わせて引き下げました。終盤はクリア数に応じて段階的に出力想定が上がる形へ調整し、ボスは終盤向けの高い進捗要求へ見直しました。" },
      { kind: "summary", topic: "stage", name: "第14期「長期持久戦」", title: "中規模報酬を調整", description: "中規模報酬のチームビルディングをペアプロへ変更しました。大量ドローに偏らず、進捗と手札補充を両立する選択肢になります。" },
    ],
  },
  {
    date: "2026.08.25",
    title: "ステージ",
    changes: [
      { kind: "summary", topic: "stage", name: "第14期「長期持久戦」", title: "追加" },
      {
        kind: "summary",
        topic: "stage",
        name: "第13期「連携と自律」",
        title: "追加",
      },
      {
        kind: "summary",
        topic: "stage",
        name: "第12期「選択と集中」",
        title: "マップを拡張",
        description: "序盤から中盤へつながる中央ルートを追加し、経路と選択肢を増やした。",
      },
    ],
  },
  {
    date: "2026.08.24",
    title: "結果画面",
    changes: [
      {
        kind: "summary",
        name: "ゲームオーバー",
        title: "同じステージの再挑戦とステージ選択を分けた",
        description: "「もう一度このステージを遊ぶ」はそのステージをすぐ開始し、「ステージセレクトに戻る」はステージ一覧へ戻る。",
      },
    ],
  },
  {
    date: "2026.08.24",
    title: "ステージ",
    changes: [
      {
        kind: "summary",
        topic: "stage",
        name: "第12期「選択と集中」",
        title: "追加",
      },
    ],
  },
  {
    date: "2026.08.23",
    title: "報酬プールの絞り込み",
    changes: [
      {
        kind: "summary",
        name: "報酬プール",
        title: "第11期は各規模4種に絞り込み",
        description: "第11期では小規模は中堅・業務フロー整備・有給休暇・肩たたき、中規模は中堅・業務分担・突貫工事・チームビルディング、大規模はベテラン・全社方針・総動員・研修のみ出現。第1〜10期は従来どおり。",
      },
    ],
  },
  {
    date: "2026.08.23",
    title: "ステージ",
    changes: [
      {
        kind: "summary",
        topic: "stage",
        name: "第11期",
        title: "追加",
      },
    ],
  },
  {
    date: "2026.08.23",
    title: "ステージ",
    changes: [
      {
        kind: "summary",
        topic: "stage",
        name: "第10期",
        title: "追加",
      },
    ],
  },
  {
    date: "2026.08.22",
    title: "ステージ",
    changes: [
      {
        kind: "summary",
        topic: "stage",
        name: "第9期",
        title: "追加",
      },
    ],
  },
  {
    date: "2026.08.22",
    title: "イベントマスの調整",
    changes: [
      {
        kind: "summary",
        name: "バランス",
        title: "出現条件を見直し",
      },
    ],
  },
  {
    date: "2026.08.22",
    title: "報酬カードの調整",
    changes: [
      {
        kind: "card",
        cardId: "hedge",
        cardName: "リスクヘッジ",
        title: "小規模報酬プールから削除",
        before: [{ kind: "pool", label: "小規模" }],
        after: [{ kind: "pool", label: "プール外" }],
      },
      {
        kind: "card",
        cardId: "paid-leave",
        cardName: "有給休暇",
        title: "効果を強化",
        before: [{ kind: "stat", stat: "fatigue", amount: -2 }],
        after: [{ kind: "stat", stat: "action", amount: 1 }, { kind: "stat", stat: "fatigue", amount: -2 }],
      },
      {
        kind: "card",
        cardId: "village",
        cardName: "業務フロー整備",
        title: "出現プールを限定",
        before: [{ kind: "pool", label: "小規模・中規模" }],
        after: [{ kind: "pool", label: "小規模のみ" }],
      },
    ],
  },
];
